import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthProvider';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { User, LogOut } from 'lucide-react';
import { SEOHead } from '../components/seo/SEOHead';

export const AccountPage: React.FC = () => {
  const { session, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      setMessage('Signed in successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: undefined, // Disable email confirmation
        }
      });

      if (error) throw error;
      
      // Check if user is immediately confirmed (email verification disabled)
      if (data.user && !data.user.email_confirmed_at) {
        setMessage('Account created successfully! You can now sign in.');
      } else {
        setMessage('Account created! You are now signed in.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/account`,
      });

      if (error) throw error;
      setMessage('Password reset email sent! Check your inbox for instructions.');
      setShowPasswordReset(false);
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (session?.user) {
    return (
      <>
        <SEOHead
          title="My Account | FreeQRCodeNow"
          description="Manage your account and QR code settings"
        />
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-green-100 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
              <CardTitle className="flex items-center gradient-text text-2xl">
                <User className="h-6 w-6 mr-2 text-green-600" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <p className="text-lg">{session.user.email}</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">User ID</label>
                <p className="text-sm text-muted-foreground font-mono">
                  {session.user.id}
                </p>
              </div>

              <div className="pt-4 border-t">
                <Button onClick={handleSignOut} variant="outline" className="border-2 hover:border-red-300 hover:text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Sign In | FreeQRCodeNow"
        description="Sign in or create an account to manage your dynamic QR codes"
      />
      <div className="space-y-8">
        <div className="max-w-md mx-auto">
          <Card className="border-2 border-green-100 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
            <CardTitle className="text-center gradient-text text-2xl">
              {isSignUp ? 'Create Account' : 'Sign In'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={isSignUp ? handleSignUp : handleSignIn} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {message && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-green-600 text-sm">{message}</p>
                </div>
              )}

              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <Input
                label="Password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <Button type="submit" className="w-full btn-gradient" disabled={isSubmitting}>
                {isSubmitting
                  ? 'Please wait...'
                  : isSignUp
                  ? 'Sign Up'
                  : 'Sign In'}
              </Button>

              <div className="text-center pt-4 space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setError(null);
                    setMessage(null);
                    setShowPasswordReset(false);
                  }}
                  className="text-sm text-green-600 hover:underline block font-medium"
                >
                  {isSignUp
                    ? 'Already have an account? Sign in'
                    : "Don't have an account? Sign up"}
                </button>
                
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordReset(!showPasswordReset);
                      setError(null);
                      setMessage(null);
                    }}
                    className="text-sm text-gray-600 hover:underline block"
                  >
                    Forgot your password?
                  </button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Password Reset Form */}
        {showPasswordReset && (
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-center">Reset Password</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-600 text-sm">{error}</p>
                    </div>
                  )}

                  {message && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-green-600 text-sm">{message}</p>
                    </div>
                  )}

                  <p className="text-sm text-gray-600 text-center">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>

                  <Input
                    label="Email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />

                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Reset Email'}
                  </Button>

                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setError(null);
                        setMessage(null);
                      }}
                      className="text-sm text-gray-600 hover:underline"
                    >
                      Back to Sign In
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </div>
    </>
  );
};


